import dayjs from 'dayjs';
import Commons from '../../../../Services/Common/Common';

export const M_PER_NEW_01_Schema = {
  fields: {
    baseInfo: {
      pmCd: { initial: '', label: '허가서코드' },
      workStartDate: {
        initial: dayjs()
          .set('hour', 9)
          .startOf('hour')
          .format('YYYY-MM-DD HH:mm'),
        label: '작업 시작일시',
        required: 'Y',
        group: 'work',
        step: 1,
        order: 10,
        type: 'datetime',
        role: 'workStartDate',
      },
      workEndDate: {
        initial: dayjs()
          .set('hour', 18)
          .startOf('hour')
          .format('YYYY-MM-DD HH:mm'),
        label: '작업 종료일시',
        required: 'Y',
        group: 'work',
        step: 1,
        order: 20,
        type: 'datetime',
        role: 'workEndDate',
      },
      workDetail: {
        initial: '',
        label: '작업 내용',
        required: 'Y',
        group: 'work',
        step: 1,
        order: 40,
        placeholder: '작업 내용을 입력하세요',
        maxLength: 256,
      },
      workPlace: {
        initial: '',
        label: '작업장소',
        required: 'Y',
        group: 'work',
        step: 1,
        order: 50,
        placeholder: '작업장소를 입력하세요',
        maxLength: 100,
      },
      workList: {
        initial: '',
        label: '작업인원',
        group: 'work',
        step: 1,
        order: 100,
        type: 'workerList',
        role: 'workList',
      },
      workMemberNo: {
        initial: '',
        label: '작업인원 수',
        group: 'work',
        step: 1,
        order: 60,
        maxLength: 11,
        inputMode: 'numeric',
      },
      workType: {
        initial: '',
        label: '작업종류',
        required: 'Y',
        group: 'work',
        step: 2,
        order: 999,
      },
      workItemId: {
        initial: '',
        label: '작업종류아이템ID',
        required: 'Y',
        group: 'work',
        step: 2,
        order: 1000,
      },
      etcRemarks: {
        initial: {},
        label: '작업별 특이사항',
        step: 6,
      },
    },
    codeInfo: {
      workTypes: { initial: [], label: '작업종류목록' },
      risks: {
        initial: [],
        label: '위험요인목록',
        codeKey: 'riskCode',
        nameKey: 'riskNm',
      },
      equips: {
        initial: [],
        label: '보호구목록',
        codeKey: 'workEquipCode',
        nameKey: 'workEquipNm',
      },
      preparations: {
        initial: [],
        label: '준비사항목록',
        codeKey: 'preparationCode',
        nameKey: 'preparationNm',
      },
    },
  },

  mapper: {
    search1: {
      CustCd: (context) => Commons.EncryptAES(context.userInfo?.custCd),
    },
    search2: {
      PmCd: (context) => Commons.EncryptAES(context.pmCd),
      WorkDate: (context) => Commons.EncryptAES(context.search?.startDate),
      UserId: (context) => context.userInfo?.userid,
    },
    save1: {
      PmCd: (context) => Commons.EncryptAES(context.baseInfo?.pmCd || ''),
      UserId: (context) => context.userInfo?.userid,
      CustCd: (context) => Commons.EncryptAES(context.userInfo?.custCd || ''),
      DepCd: (context) => Commons.EncryptAES(context.userInfo?.depCd || ''),
      ReqId: (context) => context.userInfo?.userid,
      ReqSignPath: (context) => Commons.EncryptAES(context.reqSignPath || ''),
      TotalStartDate: (context) =>
        Commons.EncryptAES(
          dayjs(context.baseInfo.workStartDate).format('YYYY-MM-DD')
        ),
      TotalEndDate: (context) =>
        Commons.EncryptAES(
          dayjs(context.baseInfo.workEndDate).format('YYYY-MM-DD')
        ),
      WorkLoc: (context) =>
        Commons.EncryptAES(context.baseInfo.workPlace || ''),
      WorkContent: (context) =>
        Commons.EncryptAES(
          context.baseInfo.workDetail || context.baseInfo.workContent || ''
        ),
      WorkMemberNo: (context) => {
        const workerString = context.baseInfo.workList || '';

        const workerCount = workerString
          .split(',')
          .map((name) => name.trim())
          .filter((name) => name !== '').length;

        return Commons.EncryptAES(String(workerCount));
      },
      PartnerNm: (context) =>
        Commons.EncryptAES(
          context.baseInfo.constructionNm || context.baseInfo.compNm || ''
        ),
      PartnerManager: (context) =>
        Commons.EncryptAES(
          context.baseInfo.siteManager || context.baseInfo.mgrNm || ''
        ),
      PartnerTel: (context) =>
        Commons.EncryptAES(
          context.baseInfo.sitePhone || context.baseInfo.mgrTel || ''
        ),
      WorkerList: (context) =>
        Commons.EncryptAES(context.baseInfo.workList || ''),
      SmId: (context) => {
        const id =
          context.approvalLine?.safetyManager?.smId || context.smId || '';
        return Commons.EncryptAES(id);
      },
      WitnessId: (context) => {
        const id = context.approvalLine?.observer?.id || '';
        return Commons.EncryptAES(id);
      },
      Status: (context) => Commons.EncryptAES('REQ'),
      EtcRemark: (context) => Commons.EncryptAES(''),
      CenterIds: (context) => {
        const heads = context.approvalLine?.depHeads || [];
        return heads.map((h) => Commons.EncryptAES(h.headId));
      },

      DetailRows: (context) => {
        const allTypes = context.codeInfo?.workTypes || [];
        const selectedWorkTypeCodes = new Set(
          (Array.isArray(context.baseInfo?.workTypes)
            ? context.baseInfo.workTypes
            : []
          ).map((c) => String(c))
        );

        const selectedTypes = allTypes.filter((t) =>
          selectedWorkTypeCodes.has(String(t.workType))
        );

        return selectedTypes.map((type) => {
          const currentId = String(type.workItemId ?? '');

          return {
            WorkItemId: Commons.EncryptAES(currentId),
            StrTime: Commons.EncryptAES(
              dayjs(context.baseInfo.workStartDate).format('HH:mm')
            ),
            EndTime: Commons.EncryptAES(
              dayjs(context.baseInfo.workEndDate).format('HH:mm')
            ),
            EtcRemark: Commons.EncryptAES(
              context.baseInfo?.etcRemarks?.[currentId] || ''
            ),
            RiskRows: (context.codeInfo.risks || [])
              .filter((r) => String(r.workItemId) === currentId)
              .map((r) => ({
                RiskCode: Commons.EncryptAES(String(r.riskCode)),
                ItemCheck: Commons.EncryptAES(r.itemCheck === 'Y' ? '0' : '1'),
              })),

            EquipRows: (context.codeInfo.equips || [])
              .filter((e) => String(e.workItemId) === currentId)
              .map((e) => ({
                WorkEquipCode: Commons.EncryptAES(String(e.workEquipCode)),
                ItemCheck: Commons.EncryptAES(e.itemCheck === 'Y' ? '0' : '1'),
              })),

            PrepRows: (context.codeInfo.preparations || [])
              .filter((p) => String(p.workItemId) === currentId)
              .map((p) => ({
                PreparationCode: Commons.EncryptAES(String(p.preparationCode)),
                ItemCheck: Commons.EncryptAES(p.itemCheck === 'Y' ? '0' : '1'),
              })),
          };
        });
      },
    },
  },
};
